import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface PortalMessage {
  id: string;
  clinic_id: string;
  patient_id: string;
  sender: 'patient' | 'clinic';
  sender_user_id: string | null;
  sender_name: string | null;
  content: string;
  read_at: string | null;
  read_by: string | null;
  created_at: string;
  updated_at: string;
}

export const portalMessagesQueryKey = (clinicId: string | null) =>
  ['portal', 'messages', clinicId] as const;

export function usePortalMessages(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: portalMessagesQueryKey(clinicId ?? null),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<PortalMessage[]> => {
      if (!clinicId) return [];
      
      const { data, error } = await supabase.rpc('get_portal_messages', {
        p_clinic_id: clinicId,
      });

      if (error) throw error;
      
      return (data ?? []) as PortalMessage[];
    },
    staleTime: 15_000,
  });
}

export function useSendPortalMessage() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (params: { clinic_id: string; content: string }) => {
      const { data, error } = await supabase.rpc('send_portal_message', {
        p_clinic_id: params.clinic_id,
        p_content: params.content,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      // Invalidate the messages query for this clinic
      queryClient.invalidateQueries({
        queryKey: portalMessagesQueryKey(variables.clinic_id),
      });
    },
  });
}

export function useMarkPortalMessageRead() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (params: { message_id: string; clinic_id: string }) => {
      const { data, error } = await supabase.rpc('mark_portal_message_read', {
        p_message_id: params.message_id,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      // Invalidate the messages query for this clinic
      queryClient.invalidateQueries({
        queryKey: portalMessagesQueryKey(variables.clinic_id),
      });
    },
  });
}
